import React, { useMemo } from 'react';
import { PiArrowSquareOutLight, PiDotsThreeVertical } from 'react-icons/pi';
import type { CatalogProperty } from 'src/api/generated';
import type { PropertyPortalPublication } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { ProviderBrandAvatar } from 'src/modules/shared/components/molecules/provider-brand-avatar';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { usePropertySyncTimeline } from '../../hooks/use-property-sync-timeline';
import { PropertySyncTimeline } from './property-sync-timeline';
import { PropertyEditorSectionCard } from './property-editor-section-card';
import { PropertySyncManagedNotice } from './property-sync-managed-notice';
import {
  getPropertySourceProviderLabelKey,
  type SupportedPropertySourceProviderId,
} from '../../utils/property-source-integration.util';

interface PropertySyncOwnershipCardProps {
  property: CatalogProperty;
  publications: PropertyPortalPublication[];
  providerId: SupportedPropertySourceProviderId;
  repairSync?: {
    canRepairSync: boolean;
    loading: boolean;
    onRepairSync: () => void | Promise<void>;
  };
}

export const PropertySyncOwnershipCard: React.FC<
  PropertySyncOwnershipCardProps
> = ({ property, publications, providerId, repairSync }) => {
  const { t } = useI18n();
  const { items, loading } = usePropertySyncTimeline({
    enabled: true,
    propertyId: property.id,
    publications,
  });

  const providerPublications = publications.filter(
    (publication) => publication.portal === providerId
  );
  const syncCode = property.sourceCode?.trim() ?? property.sourceId?.trim();
  const providerLabel = t(getPropertySourceProviderLabelKey(providerId));
  const shouldShowSourceSummary =
    providerPublications.length === 0 && !!syncCode;
  const sourceUrl = property.sourceUrl?.trim() ?? null;
  const syncActions = useMemo<DropdownItem[]>(() => {
    if (!repairSync?.canRepairSync) {
      return [];
    }

    return [
      {
        content: t('catalog.properties.editor.publishing.actions.repairSync'),
        onClick: () => {
          void repairSync.onRepairSync();
        },
      },
    ];
  }, [repairSync, t]);
  const syncActionsMenu =
    syncActions.length > 0 ? (
      <MenuDropdown
        items={syncActions}
        placement="bottom-end"
        isLoading={repairSync?.loading ?? false}
        handlerComponent={
          <Button
            type="button"
            variant="secondary"
            appearance="soft"
            size="md"
            isIconOnly
            aria-label={t('catalog.properties.header.actionsMenu')}
            disabled={repairSync?.loading ?? false}
          >
            <PiDotsThreeVertical className="h-4 w-4" />
          </Button>
        }
      />
    ) : null;

  return (
    <PropertyEditorSectionCard
      title={t('catalog.properties.editor.publishing.syncCardTitle')}
      actions={syncActionsMenu}
    >
      <PropertySyncManagedNotice providerLabel={providerLabel} t={t} />

      {shouldShowSourceSummary ? (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3">
          <ProviderBrandAvatar providerId={providerId} size="sm" />
          <div className="min-w-0 flex-1">
            <Text size="tiny" className="text-slate-500">
              {t(
                'catalog.properties.editor.publishing.syncOwnership.sourceCode'
              )}
            </Text>
            <Text
              size="small"
              weight="medium"
              className="truncate text-slate-900"
            >
              {syncCode}
            </Text>
          </div>
          {sourceUrl ? (
            <Tooltip content={t('propertyLinks.openSource')} size="xs">
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="sm"
                isIconOnly
                aria-label={t('propertyLinks.openSource')}
                onClick={() => window.open(sourceUrl, '_blank')}
              >
                <PiArrowSquareOutLight className="h-4 w-4" />
              </Button>
            </Tooltip>
          ) : null}
        </div>
      ) : null}

      <div>
        <PropertySyncTimeline items={items} loading={loading} />
      </div>
    </PropertyEditorSectionCard>
  );
};
